import React from 'react';
import { defaultTo, get } from 'lodash';
import { useFeatureToggle, DISCOUNT_LABELS } from '@css-xp/base';
import { v4 as generateUuid } from 'uuid';
import {
  ChargeQuantityDto,
  OrderBillEstimateItemResponseDto,
  OrderCreationItemDto,
} from '@atlassian/commerce-ccp-gateway-client';
import { mapEstimate } from '@css-xp/ppc-utils';

import { SubscriptionRelationshipType } from '@css-xp/ppc-constants';
import { getEntitlementDefaults, getOrderEstimateForPartnerOrderReview } from '@atlassian/temp-proxies';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants/src/ppc-feature-gates';
import { EntitlementDefaultsResponse, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { GetOrderIntentEstimateRequestForPartnerOrderReview } from '@atlassian/cssxp-typescommon';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { tableErrorMessage } from '@css-xp/ppc-common';
import { PartnerOrderEstimate } from '@css-xp/ppc-types';
import { usePartnerCatalogueContext } from '../context/partner-catalogue-context';
import { ActualEstimates, AppPromoCodeDetails, PartnerQuoteLineItem, PartnerQuoteLineItems } from '../types';
import { CheckoutOperation, usePartnerCheckoutNotification } from './use-place-order-notification';
import { getPromotionsPayloadItems } from '../utils/get-promotions-payload-items';

export const JIRA_RELATIONSHIP_OFFERING_KEY = '009f8789-c10c-4366-9420-f712d02a6499';
export const APP_PAGINATION_SIZE = 20;

const ADD_ON = 'ADD_ON';

enum EntitlementReferenceTypes {
  ORDER_ITEM = 'ORDER_ITEM',
  ENTITLEMENT = 'ENTITLEMENT',
}

export type UseProcessLineItemsForOrderIntentEstimateParams = {
  lineItemsData: PartnerQuoteLineItems;
};

export const useProcessLineItemsForOrderIntentEstimate = ({
  lineItemsData,
}: UseProcessLineItemsForOrderIntentEstimateParams): {
  processLineItemsForOrderEstimate: (
    _lineItems: PartnerQuoteLineItems,
    _productToPromotionsMap?: Record<string, string | undefined> | undefined,
    _pricingEligibility?: string
  ) => OrderCreationItemDto[];
  getEstimate: (_params: {
    intentEstimateRequestPayload: GetOrderIntentEstimateRequestForPartnerOrderReview;
    lineItems?: PartnerQuoteLineItems;
    showErrorMessage?: boolean;
  }) => void;
  processEstimateResponse: (_params: {
    response: OrderBillEstimateItemResponseDto;
    lineItemsData: PartnerQuoteLineItems;
  }) => {
    estimates: Map<string, any>;
    taxItemsMap: Map<string, { tax: number; taxPercent: number }>;
    adminTaxDetailsMap: Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>;
    partnerPricing: PartnerOrderEstimate.OverallPricing;
  };
  orderIntentId: string;
  actualEstimates: Map<string, any>;
  totalAmountToPay: number | undefined | null;
  totalDiscountApplied: number | undefined | null;
  totalPartnerDiscountApplied: number | undefined | null;
  totalAmountAfterDiscountAndAdminFeeButBeforeTaxes: number | undefined | null;
  hasBillEstimateError: boolean;
  totalTaxPayable: number;
  orderCreationItems: OrderCreationItemDto[] | undefined;
  setLineItems: (_lineItems: PartnerQuoteLineItems) => void;
  taxBreakUp: { taxIdLabel: string; tax: number }[];
  adminTaxBreakUp: Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>;
  resetAllLineItemValues: () => void;
  getOrderDefaults: (_params: {
    offeringId: string;
    transactionAccountId: string;
  }) => Promise<EntitlementDefaultsResponse>;
} => {
  const [orderIntentId] = React.useState(generateUuid());
  const [lineItems, setLineItems] = React.useState<PartnerQuoteLineItems>(lineItemsData);
  const [orderCreationItems, setOrderCreationItems] = React.useState<OrderCreationItemDto[]>();
  const [actualEstimates, setActualEstimates] = React.useState<ActualEstimates>(new Map());
  const [
    totalAmountAfterDiscountAndAdminFeeButBeforeTaxes,
    setTotalAmountAfterDiscountAndAdminFeeButBeforeTaxes,
  ] = React.useState<number | undefined | null>(null);
  const [hasBillEstimateError, setBillEstimateError] = React.useState<boolean>(false);
  const [totalAmountToPay, setTotalAmountToPay] = React.useState<number | undefined | null>(null);
  const [totalDiscountApplied, setTotalDiscountApplied] = React.useState<number | undefined | null>(null);
  const [totalPartnerDiscountApplied, setTotalPartnerDiscountApplied] = React.useState<number | undefined | null>(null);
  const [totalTaxPayable, setTotalTaxPayable] = React.useState<number>(0);
  const [taxBreakUp, setTaxBreakUp] = React.useState<{ taxIdLabel: string; tax: number; taxPercent: number }[]>([]);
  const [adminTaxDetailsMap, setAdminTaxDetailsMap] = React.useState<
    Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>
  >(new Map());

  const [isEnabled] = useFeatureToggle();
  const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);

  const resetAllValues = () => {
    setTotalAmountAfterDiscountAndAdminFeeButBeforeTaxes(null);
    setActualEstimates(new Map());
  };

  const { notifyFailure } = usePartnerCheckoutNotification();

  const {
    familyContainerId: existingFamilyContainerId,
    setEstimatesToBeRefreshed,
    getPricingPlanIdBasedOnPricingEligibilityAndOffering,
    getAppsPromotionIds,
    getAppsParentEntitlement,
  } = usePartnerCatalogueContext();
  const [jiraFamilyContainerItemId] = React.useState(existingFamilyContainerId ?? generateUuid());
  const [familyContainer] = React.useState<OrderCreationItemDto>({
    itemId: jiraFamilyContainerItemId,
    offeringId: JIRA_RELATIONSHIP_OFFERING_KEY,
    type: OrderItemType.CREATION_ORDER,
  });

  const getLineItemPromotionIds = (lineItem: any, promotionId?: string, isApp?: boolean) => {
    const promotionIds = [];

    // pushing partner promotion id
    if (!lineItem.isFreeProduct && promotionId) {
      promotionIds.push({ promotionDefinition: { promotionId } });
    }

    // pushing apps marketplace promotion id
    if (isApp) {
      const appPromotionCode = defaultTo(getAppsPromotionIds(lineItem.id) || lineItem?.promoCode, '');
      if (appPromotionCode) {
        // here we are passing promotionId with empty data because promotionId is mandatory in PromotionDefinitionDto
        // also we cannot send actual promotion id, we can pass either of promotionId or promotionCode otherwise API fails
        promotionIds.push({ promotionDefinition: { promotionId: '', promotionCode: appPromotionCode } });
      }
    }
    return promotionIds;
  };

  // get all promotions applied on line item
  const getLineItemsPromotions = (lineItem: any, partnerPromotionId?: string, isApp?: boolean) => {
    const promotions = [];
    //this is used for old catalog where we have promotionId map used in catalog
    const promotionIds = getLineItemPromotionIds(lineItem, partnerPromotionId, isApp);
    //where we get list of all promotions applied on line item and convert it to promotion object
    const nonPartnerPromotions = getPromotionsPayloadItems(
      lineItem.promotions as AppPromoCodeDetails[],
      lineItem?.isFreeProduct
    );
    if (promotionIds?.length) {
      promotions.push(...promotionIds);
    }
    if (nonPartnerPromotions?.length) {
      promotions.push(...nonPartnerPromotions);
    }
    return promotions as any; //TODO: this is a temporary fix. Need to fix the type of promotions
  };

  const processEstimateResponse = ({
    response,
    lineItemsData,
  }: {
    response: OrderBillEstimateItemResponseDto;
    lineItemsData: PartnerQuoteLineItems;
  }): {
    estimates: Map<string, any>;
    taxItemsMap: Map<string, { tax: number; taxPercent: number }>;
    adminTaxDetailsMap: Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>;
    partnerPricing: PartnerOrderEstimate.OverallPricing;
  } => {
    const mappedEstimate = mapEstimate(response);
    const estimates = new Map();
    const taxItemsMap = new Map<string, { tax: number; taxPercent: number }>();
    const adminTaxDetailsMap = new Map<string, { adminTaxItemLabel: string; totalAdminTax: number }>();
    const processLineItem = (lineItem: PartnerQuoteLineItem) => {
      const relatedEstimateItem = mappedEstimate.items.find((item) => item.itemId === lineItem.lineItemId);
      const typeOfAdjustments = [
        ...new Set(relatedEstimateItem?.partnerPricing?.adjustments?.map((adjustment) => adjustment?.type)),
      ];
      const allPromotionsApplied = typeOfAdjustments.map((typeOfAdjustment) => {
        const adjustments = relatedEstimateItem?.partnerPricing?.adjustments?.filter(
          (adjustment) => adjustment.type === typeOfAdjustment
        );
        const percent = adjustments?.[0]?.percent;
        const totalAdjustment = adjustments?.reduce((acc, adjustment) => {
          return acc + (adjustment?.amount ?? 0);
        }, 0);

        return {
          typeOfAdjustment,
          adjustmentLabel:
            typeOfAdjustment === 'PROMO_CODE' ? DISCOUNT_LABELS.PROMO_CODE : DISCOUNT_LABELS.PARTNER_DISCOUNT,
          totalAdjustment,
          percent,
        };
      });
      const estimateDetails = {
        grandTotal: relatedEstimateItem?.partnerPricing.grandTotal,
        listPrice: relatedEstimateItem?.partnerPricing.listPrice,
        totalDiscount: relatedEstimateItem?.partnerPricing.totalDiscount,
        totalPartnerDiscount: relatedEstimateItem?.partnerPricing.totalPartnerDiscount,
        discountPercent: relatedEstimateItem?.partnerPricing?.adjustments?.[0]?.percent,
        total: relatedEstimateItem?.partnerPricing.totalExcludingTax,
        totalTax: relatedEstimateItem?.partnerPricing.totalTax,
        estimateIncludingPartnerDiscount: relatedEstimateItem?.partnerPricing?.totalPriceAfterDiscountAndBeforeTaxes,
        allPromotionsApplied,
        isAdminFeeApplicable: relatedEstimateItem?.isAdminFeeApplicable,
        ...(relatedEstimateItem?.isAdminFeeApplicable && {
          adminFee: relatedEstimateItem?.adminFee?.amount,
          percentageOfAdminFee: relatedEstimateItem?.percentageOfAdminFee,
        }),
      };
      relatedEstimateItem?.partnerPricing.taxItems.forEach((taxItem) => {
        if (taxItem.taxAmountLabel) {
          const taxValue = taxItem?.tax ?? 0;
          if (taxItemsMap.has(taxItem.taxAmountLabel)) {
            const currentTaxValueForCurrentLabel = taxItemsMap.get(taxItem.taxAmountLabel);
            if (currentTaxValueForCurrentLabel) {
              taxItemsMap.set(taxItem.taxAmountLabel, {
                ...currentTaxValueForCurrentLabel,
                tax: (currentTaxValueForCurrentLabel?.tax ?? 0) + taxValue,
              });
            }
            return;
          }
          taxItemsMap.set(taxItem.taxAmountLabel, {
            tax: taxValue,
            taxPercent: taxItem.taxPercent ?? 0,
          });
        }
      });
      const uniqueTaxLabels = new Set<string>();
      const taxLabelWiseTaxInfo = relatedEstimateItem?.partnerPricing.taxItems.reduce<
        Record<string, { tax: number; taxPercent: number }>
      >((acc, taxInfo) => {
        if (taxInfo.taxAmountLabel) {
          uniqueTaxLabels.add(taxInfo.taxAmountLabel);
          if (acc[taxInfo.taxAmountLabel]) {
            acc[taxInfo.taxAmountLabel] = {
              tax: acc[taxInfo.taxAmountLabel].tax + (taxInfo?.tax ?? 0),
              taxPercent: taxInfo.taxPercent ?? 0,
            };
            return acc;
          }
          acc[taxInfo.taxAmountLabel] = {
            tax: taxInfo.tax ?? 0,
            taxPercent: taxInfo.taxPercent ?? 0,
          };
          return acc;
        }
        return acc;
      }, {});
      estimates.set(lineItem.lineItemId, {
        ...estimateDetails,
        ...{
          itemLevelTaxInfo: taxLabelWiseTaxInfo
            ? [...uniqueTaxLabels].map((taxLabel) => {
                return [taxLabel, taxLabelWiseTaxInfo[taxLabel]];
              })
            : [],
        },
      });
      if (relatedEstimateItem?.partnerPricing.dstTaxItems.length) {
        const adminTaxItemLabel = relatedEstimateItem?.partnerPricing.dstTaxItems[0].taxAmountLabel;
        if (adminTaxItemLabel) {
          adminTaxDetailsMap.set(lineItem.lineItemId, { adminTaxItemLabel, totalAdminTax: 0 });
          const totalAdminTax = relatedEstimateItem?.partnerPricing.dstTaxItems.reduce((accumulator, { tax = 0 }) => {
            return accumulator + tax;
          }, 0);
          adminTaxDetailsMap.set(lineItem.lineItemId, { adminTaxItemLabel, totalAdminTax });
        }
      }
    };
    lineItemsData.forEach((lineItem: PartnerQuoteLineItem) => {
      processLineItem(lineItem);
      lineItem.appLineItems.forEach(processLineItem);
      const totalPriceForRelatedApps = lineItem.appLineItems.reduce<number>((acc, appLineItem) => {
        return acc + (estimates.get(appLineItem.lineItemId)?.grandTotal ?? 0);
      }, 0);
      const lineItemEstimate = estimates.get(lineItem.lineItemId);
      if (lineItemEstimate) {
        estimates.set(lineItem.lineItemId, {
          ...lineItemEstimate,
          totalPriceForRelatedApps,
        });
      }
    });
    const partnerPricing = mappedEstimate.partnerPricing;

    return {
      partnerPricing,
      estimates,
      taxItemsMap,
      adminTaxDetailsMap,
    };
  };

  const calculateEstimate = async ({
    intentEstimateRequestPayload,
    lineItemsData = lineItems,
    showErrorMessage = true,
  }: {
    intentEstimateRequestPayload: GetOrderIntentEstimateRequestForPartnerOrderReview;
    lineItemsData?: PartnerQuoteLineItems;
    showErrorMessage?: boolean;
  }) => {
    try {
      const response = await getOrderEstimateForPartnerOrderReview(intentEstimateRequestPayload);
      const { partnerPricing, estimates, taxItemsMap, adminTaxDetailsMap } = processEstimateResponse({
        response,
        lineItemsData,
      });
      setTotalAmountToPay(partnerPricing.grandTotal);
      setTotalDiscountApplied(partnerPricing.totalDiscount);
      setTotalPartnerDiscountApplied(partnerPricing.totalPartnerDiscount);
      setTotalAmountAfterDiscountAndAdminFeeButBeforeTaxes(partnerPricing.totalExcludingTax);
      setActualEstimates(estimates);

      setTotalTaxPayable(partnerPricing.totalTax);
      setBillEstimateError(false);
      setTaxBreakUp(
        [...taxItemsMap.entries()].map(([taxIdLabel, { tax, taxPercent }]) => {
          return { taxIdLabel, tax, taxPercent };
        })
      );
      setAdminTaxDetailsMap(adminTaxDetailsMap);
      setEstimatesToBeRefreshed(false);
    } catch (err) {
      setBillEstimateError(true);
      showErrorMessage && notifyFailure(tableErrorMessage(), CheckoutOperation.CalculateEstimate);
    }
  };

  const defaultChargeQuantityToOneWhenZero = (chargeQuantities: ChargeQuantityDto[]) => {
    return chargeQuantities.map((chargeQuantity) => {
      return {
        ...chargeQuantity,
        quantity: Number(chargeQuantity.quantity) || 1,
      };
    });
  };

  const processLineItemsForOrderEstimate = (
    lineItems: PartnerQuoteLineItems,
    productToPromotionsMap?: Record<string, string | undefined> | undefined,
    pricingEligibility?: string
  ) => {
    // const paidLineItems = lineItems.filter((lineItem) => !isFreeEdition(lineItem.offeringName));
    const orderIntentItems: OrderCreationItemDto[] = [];
    let hasJiraAppInLineitems = false;
    const jiraLineItems = lineItems.filter(
      (lineItem) =>
        lineItem.isItAJiraProduct ||
        isJiraProduct(defaultTo(get(getAppsParentEntitlement(lineItem.productKey), 'productId'), ''))
    );
    const nonJiraLineItems = lineItems.filter(
      (lineItem) =>
        !lineItem.isItAJiraProduct &&
        !isJiraProduct(defaultTo(get(getAppsParentEntitlement(lineItem.productKey), 'productId'), ''))
    );

    jiraLineItems.forEach((lineItem) => {
      const partnerPromotionId = productToPromotionsMap?.[lineItem.productKey];
      const isItAnApp = lineItem?.isApp;
      if (!hasJiraAppInLineitems && isItAnApp) {
        hasJiraAppInLineitems = true;
      }
      const promotions = getLineItemsPromotions(lineItem, partnerPromotionId, isItAnApp);

      const parentEntitlementId = defaultTo(
        get(getAppsParentEntitlement(lineItem.productKey), 'relatesFromEntitlements[0].entitlementId'),
        ''
      );
      const hasParentEntitlementLink = lineItem?.isApp && parentEntitlementId;
      const nextBillingAnchorDate = defaultTo(
        get(getAppsParentEntitlement(lineItem.productKey), 'nextBillingTimestamp'),
        0
      );
      const pricingPlanId = pricingEligibility
        ? getPricingPlanIdBasedOnPricingEligibilityAndOffering(lineItem.productKey, pricingEligibility.toUpperCase())
        : undefined;

      /**
       * Changes done for PDF Billing period:
       *
       * Added parameter showStartDate to show Billing period in PDF in below format
       * Starting {nextBillDate} (Monthly)
       * When showStartDate is false then it will show only (Monthly).
       *
       * Added parameter hideBillingPeriod to toggle between this above change and old behaviour in PDF w.r.t FF.
       * Old behaviour shows {Start date} - {End date} for all scenarios.
       */
      orderIntentItems.push({
        itemId: lineItem.lineItemId,
        pricingPlanId: lineItem.pricingPlanId,
        type: OrderItemType.CREATION_ORDER,
        cloudSite: lineItem.cloudSite,
        entitlementNumber: lineItem.entitlementNumber,
        entitlementId: lineItem.id,
        orgName: lineItem.orgName,
        showStartDate: lineItem.showStartDate,
        hideBillingPeriod: lineItem.hideBillingPeriod,
        subscriptionStartTime: lineItem.subscriptionStartTime,
        nextBillingAnchorTimestamp: lineItem.nextBillingAnchorTimestamp,
        actualNextBillDate: lineItem.actualNextBillDate,
        offeringId: lineItem.offeringKey,
        ...(promotions.length && {
          promotions,
        }),
        relatesFromEntitlements: [
          hasParentEntitlementLink || existingFamilyContainerId
            ? {
                referenceType: EntitlementReferenceTypes.ENTITLEMENT,
                entitlementId: hasParentEntitlementLink ? parentEntitlementId : existingFamilyContainerId,
                relationshipType: hasParentEntitlementLink
                  ? SubscriptionRelationshipType.APP_DEPENDENCE
                  : SubscriptionRelationshipType.FAMILY_CONTAINER,
              }
            : {
                referenceType: EntitlementReferenceTypes.ORDER_ITEM,
                itemId: jiraFamilyContainerItemId,
                relationshipType: SubscriptionRelationshipType.FAMILY_CONTAINER,
              },
        ] as any,
        optedUsageOptions: {
          trial: {
            skipTrial: true,
          },
          ...(!lineItem.isFreeProduct && {
            chargingDetails: {
              pricingPlanId: pricingPlanId ?? lineItem.pricingPlanId,
              chargeQuantities: defaultChargeQuantityToOneWhenZero(
                (lineItem.chargeQuantities as unknown) as ChargeQuantityDto[]
              ),
            },
          }),
        },
        ...(lineItem?.isApp &&
          nextBillingAnchorDate && {
            nextBillingAnchorTimestamp: Number(nextBillingAnchorDate),
          }),
      });
      lineItem.appLineItems
        .filter((appLineItem: any) => appLineItem.pricingPlanKey)
        .forEach((appLineItem: any) => {
          const promotionId = productToPromotionsMap?.[appLineItem.productKey];
          const promotions = getLineItemsPromotions(appLineItem, promotionId, isItAnApp);
          orderIntentItems.push({
            itemId: appLineItem.lineItemId,
            pricingPlanId: isAppEditionSupportEnabled ? appLineItem.pricingPlanKey : lineItem?.pricingPlanId,
            type: OrderItemType.CREATION_ORDER,
            cloudSite: appLineItem.cloudSite,
            entitlementNumber: appLineItem.entitlementNumber,
            entitlementId: appLineItem.id,
            orgName: appLineItem.orgName,
            showStartDate: appLineItem.showStartDate,
            hideBillingPeriod: appLineItem.hideBillingPeriod,
            subscriptionStartTime: appLineItem.subscriptionStartTime,
            nextBillingAnchorTimestamp: appLineItem.nextBillingAnchorTimestamp,
            actualNextBillDate: appLineItem.actualNextBillDate,
            offeringId: appLineItem.offeringId,
            ...(promotions.length && {
              promotions,
            }),
            optedUsageOptions: {
              trial: {
                skipTrial: true,
              },
              ...(!appLineItem.isFreeProduct && {
                chargingDetails: {
                  pricingPlanId: appLineItem.pricingPlanKey,
                  chargeQuantities: appLineItem.chargeQuantities as ChargeQuantityDto[],
                },
              }),
            },
            relatesFromEntitlements: [
              {
                referenceType: EntitlementReferenceTypes.ORDER_ITEM,
                itemId: jiraFamilyContainerItemId,
                relationshipType:
                  appLineItem?.relationshipType === ADD_ON
                    ? SubscriptionRelationshipType.ADDON_DEPENDENCE
                    : SubscriptionRelationshipType.APP_DEPENDENCE,
              },
            ] as any,
          });
        });
    });

    nonJiraLineItems.forEach((lineItem) => {
      const partnerPromotionId = productToPromotionsMap?.[lineItem.productKey];
      const isItAnApp = lineItem?.isApp;
      const promotions = getLineItemsPromotions(lineItem, partnerPromotionId, isItAnApp);
      const parentEntitlementId = defaultTo(get(getAppsParentEntitlement(lineItem.productKey), 'id'), '');
      const nextBillingAnchorDate = defaultTo(
        get(getAppsParentEntitlement(lineItem.productKey), 'nextBillingTimestamp'),
        0
      );
      const pricingPlanId = pricingEligibility
        ? getPricingPlanIdBasedOnPricingEligibilityAndOffering(lineItem.productKey, pricingEligibility.toUpperCase())
        : undefined;
      orderIntentItems.push({
        itemId: lineItem.lineItemId,
        type: OrderItemType.CREATION_ORDER,
        cloudSite: lineItem.cloudSite,
        entitlementNumber: lineItem.entitlementNumber,
        entitlementId: lineItem.id,
        orgName: lineItem.orgName,
        showStartDate: lineItem.showStartDate,
        hideBillingPeriod: lineItem.hideBillingPeriod,
        subscriptionStartTime: lineItem.subscriptionStartTime,
        nextBillingAnchorTimestamp: lineItem.nextBillingAnchorTimestamp,
        actualNextBillDate: lineItem.actualNextBillDate,
        offeringId: lineItem.offeringKey,
        ...(promotions.length && {
          promotions,
        }),
        optedUsageOptions: {
          trial: {
            skipTrial: true,
          },
          ...(!lineItem.isFreeProduct && {
            chargingDetails: {
              pricingPlanId: pricingPlanId ?? lineItem.pricingPlanId,
              chargeQuantities: defaultChargeQuantityToOneWhenZero(
                (lineItem.chargeQuantities as unknown) as ChargeQuantityDto[]
              ),
            },
          }),
        },
        ...(lineItem?.isApp &&
          parentEntitlementId && {
            relatesFromEntitlements: [
              {
                referenceType: EntitlementReferenceTypes.ENTITLEMENT,
                entitlementId: parentEntitlementId,
                relationshipType: SubscriptionRelationshipType.APP_DEPENDENCE,
              },
            ] as any,
          }),
        ...(lineItem?.isApp &&
          nextBillingAnchorDate && {
            nextBillingAnchorTimestamp: Number(nextBillingAnchorDate),
          }),
      });

      lineItem.appLineItems
        .filter((appLineItem: any) => appLineItem.pricingPlanKey)
        .forEach((appLineItem: any) => {
          const promotionId = productToPromotionsMap?.[appLineItem.productKey];
          const promotions = getLineItemsPromotions(appLineItem, promotionId, isItAnApp);

          orderIntentItems.push({
            itemId: appLineItem.lineItemId,
            type: OrderItemType.CREATION_ORDER,
            cloudSite: appLineItem.cloudSite,
            entitlementNumber: appLineItem.entitlementNumber,
            entitlementId: appLineItem.id,
            showStartDate: appLineItem.showStartDate,
            hideBillingPeriod: appLineItem.hideBillingPeriod,
            subscriptionStartTime: appLineItem.subscriptionStartTime,
            nextBillingAnchorTimestamp: appLineItem.nextBillingAnchorTimestamp,
            actualNextBillDate: appLineItem.actualNextBillDate,
            offeringId: appLineItem.offeringId,
            ...(promotions.length && {
              promotions,
            }),
            optedUsageOptions: {
              trial: {
                skipTrial: true,
              },
              ...(!appLineItem.isFreeProduct && {
                chargingDetails: {
                  pricingPlanId: appLineItem.pricingPlanKey,
                  chargeQuantities: appLineItem.chargeQuantities as ChargeQuantityDto[],
                },
              }),
            },
            relatesFromEntitlements: [
              {
                referenceType: EntitlementReferenceTypes.ORDER_ITEM,
                itemId: lineItem.lineItemId,
                relationshipType:
                  appLineItem?.relationshipType === ADD_ON
                    ? SubscriptionRelationshipType.ADDON_DEPENDENCE
                    : SubscriptionRelationshipType.APP_DEPENDENCE,
              },
            ] as any,
          });
        });
    });

    // Creation of order creation items can be done async, thus not blocking calculate estimate
    setTimeout(() => {
      let orderIntentItemsWithJiraFamilyContainer = orderIntentItems;
      // Checking this condition for jira line items and should not have any jira apps with parent entitlement
      if (jiraLineItems.length > 0 && !hasJiraAppInLineitems) {
        orderIntentItemsWithJiraFamilyContainer = existingFamilyContainerId
          ? [...orderIntentItems]
          : [familyContainer, ...orderIntentItems];
      }

      // Order intent items have skipTrial set to true, as setting it false makes OMS to return estimate as 0.
      // But while placing the order, we need to enable trial for all the monthly orders.
      const allOrderCreationItems = [...orderIntentItemsWithJiraFamilyContainer];
      const allOrderCreationItemsWithSkipTrialSetToFalse = allOrderCreationItems.map((item) => {
        // Family container doesn't need any further processing.
        if (item.itemId === jiraFamilyContainerItemId) {
          return item;
        }
        return {
          ...item,
          optedUsageOptions: {
            ...item.optedUsageOptions,
            trial: {
              skipTrial: false,
            },
          },
        };
      });

      setOrderCreationItems(allOrderCreationItemsWithSkipTrialSetToFalse);
    });
    return orderIntentItems;
  };

  const getOrderDefaults = async ({
    offeringId,
    transactionAccountId,
  }: {
    offeringId: string;
    transactionAccountId: string;
  }): Promise<EntitlementDefaultsResponse> => {
    const defaultResponse = await getEntitlementDefaults({
      offeringId,
      transactionAccountId,
      relatedEntitlementId: existingFamilyContainerId,
      relationshipType: SubscriptionRelationshipType.FAMILY_CONTAINER,
      billingCycle: 'MONTHLY',
    });
    return defaultResponse;
  };

  return {
    processLineItemsForOrderEstimate,
    getEstimate: calculateEstimate,
    orderIntentId,
    actualEstimates,
    totalAmountToPay,
    totalTaxPayable,
    totalDiscountApplied,
    totalPartnerDiscountApplied,
    totalAmountAfterDiscountAndAdminFeeButBeforeTaxes,
    hasBillEstimateError,
    orderCreationItems,
    taxBreakUp,
    adminTaxBreakUp: adminTaxDetailsMap,
    setLineItems,
    resetAllLineItemValues: resetAllValues,
    getOrderDefaults,
    processEstimateResponse,
  };
};
